import { useEffect, useMemo, useState } from "react";
import { api, voterApi, formatApiErrorDetail } from "@/lib/api";
import { AppLayout, PageHeader } from "@/components/Layout";
import { Heart, Search, ShieldCheck, CheckCircle2, Trophy, Lock } from "lucide-react";
import { toast } from "sonner";

const PRIVACY = "Your contact information will be used only to verify your Fan Favorite votes. It will not be added to marketing email or text lists without separate consent.";

const DIVS = [["Adult", "Adult Undersaddle"], ["InHand", "Adult In-Hand"], ["Youth", "Youth In-Hand"]];
const LABEL = Object.fromEntries(DIVS);

export default function FanFavorite() {
  const [config, setConfig] = useState(null);
  const [eligible, setEligible] = useState({ Adult: [], InHand: [], Youth: [] });
  const [status, setStatus] = useState(null); // {Adult:{used,horse}, Youth:{used,horse}}
  const [step, setStep] = useState("intro"); // intro | code | vote
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [division, setDivision] = useState("Adult");
  const [q, setQ] = useState("");

  const loadStatus = () => voterApi.get("/fanfav/status").then((r) => { setStatus(r.data.status); setStep("vote"); }).catch(() => localStorage.removeItem("bmm_voter_token"));

  useEffect(() => {
    api.get("/fanfav/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/fanfav/eligible").then((r) => setEligible(r.data)).catch(() => {});
    if (localStorage.getItem("bmm_voter_token")) loadStatus();
  }, []);

  const requestCode = async () => {
    setBusy(true); setError("");
    try { await api.post("/fanfav/request-code", { contact: email }); setStep("code"); toast.success("If this contact can be verified, a code has been sent."); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/fanfav/verify", { contact: email, code });
      localStorage.setItem("bmm_voter_token", data.token);
      setStatus(data.status); setStep("vote");
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const vote = async (horse) => {
    try {
      const { data } = await voterApi.post("/fanfav/vote", { division, horse_id: horse.id });
      setStatus(data.status);
      toast.success(`${LABEL[division]} vote recorded: HIP #${horse.hip_number}, ${horse.horse_name}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Vote failed"); }
  };

  const divOpen = config ? config[`${division.toLowerCase()}_open`] : false;
  const anyOpen = config && DIVS.some(([d]) => config[`${d.toLowerCase()}_open`]);
  const winners = config ? DIVS.filter(([d]) => config[`${d.toLowerCase()}_winner`]) : [];
  const list = useMemo(() => {
    const items = eligible[division] || [];
    if (!q) return items;
    const digits = q.replace(/\D/g, ""); const ql = q.toLowerCase();
    return items.filter((h) => (digits && String(h.hip_number) === digits) || (h.horse_name || "").toLowerCase().includes(ql) || (h.trainer_name || "").toLowerCase().includes(ql));
  }, [eligible, division, q]);

  return (
    <AppLayout>
      <PageHeader subtitle="Vote for your favorite" title="Fan Favorite" />

      {/* Winner cards (after publish) */}
      {winners.length > 0 && (
        <div className="space-y-2 px-4 pt-2">
          {winners.map(([d, label]) => (
            <WinnerCard key={d} label={`${label} Fan Favorite`} horse={config[`${d.toLowerCase()}_winner`]} />
          ))}
        </div>
      )}

      <p className="mx-4 mt-2 rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
        <Heart size={12} className="mr-1 inline" /> The heart Favorites feature is a personal saved list. This is the official <strong>Fan Favorite Vote</strong> — separate from judged competition scores.
      </p>

      {/* Voting window status */}
      {config && !anyOpen && winners.length === 0 && (
        <div className="mx-4 mt-3 flex items-center gap-2 rounded-xl border border-yellow-300 bg-yellow-50 p-3 text-sm font-medium text-yellow-800">
          <Lock size={16} /> Fan Favorite voting is not currently open.
          {config.voting_start && <> Opens {new Date(config.voting_start).toLocaleString()}.</>}
        </div>
      )}

      {/* INTRO / verification */}
      {step !== "vote" && anyOpen && (
        <div className="mx-4 mt-3 space-y-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-2 text-primary"><ShieldCheck size={20} className="text-secondary" /><h2 className="font-display text-lg font-bold">Verify to vote</h2></div>
          <p className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">{PRIVACY}</p>
          {step === "intro" ? (
            <>
              <input data-testid="fanfav-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address"
                className="h-12 w-full rounded-xl border border-border bg-background px-4 outline-none focus:ring-2 focus:ring-secondary" />
              <button data-testid="fanfav-send-code" disabled={busy || !email} onClick={requestCode}
                className="h-12 w-full rounded-full bg-primary font-bold text-primary-foreground active:scale-95 disabled:opacity-60">{busy ? "Sending…" : "Send verification code"}</button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">Enter the 6-digit code sent to {email}.</p>
              <input data-testid="fanfav-code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456"
                className="h-12 w-full rounded-xl border border-border bg-background px-4 text-center text-2xl tracking-[0.4em] outline-none focus:ring-2 focus:ring-secondary" />
              <button data-testid="fanfav-verify" disabled={busy || code.length < 6} onClick={verify}
                className="h-12 w-full rounded-full bg-secondary font-bold text-white active:scale-95 disabled:opacity-60">{busy ? "Verifying…" : "Verify & continue"}</button>
              <button onClick={() => setStep("intro")} className="w-full text-xs font-semibold text-muted-foreground">Use a different email</button>
            </>
          )}
          {error && <p data-testid="fanfav-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        </div>
      )}

      {/* VOTE */}
      {step === "vote" && (
        <div className="px-4 pt-3">
          {status && (
            <div className="mb-3 space-y-2">
              {DIVS.map(([d, label]) => (
                <div key={d} data-testid={`fanfav-status-${d.toLowerCase()}`} className="rounded-xl border border-border bg-card p-3 text-sm">
                  {status[d]?.used ? (
                    <p className="flex items-center gap-2 font-semibold text-green-700"><CheckCircle2 size={16} /> {label} vote recorded: HIP #{status[d].horse?.hip_number}, {status[d].horse?.horse_name}</p>
                  ) : (
                    <p className="text-muted-foreground">You may still cast your {label} Fan Favorite vote.</p>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-3 gap-1 rounded-xl border border-border bg-muted p-1">
            {DIVS.map(([d, label]) => (
              <button key={d} data-testid={`fanfav-tab-${d.toLowerCase()}`} onClick={() => setDivision(d)}
                className={`rounded-lg px-1 py-2.5 text-xs font-semibold leading-tight transition-colors sm:text-sm ${division === d ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{label}</button>
            ))}
          </div>

          <div className="relative mt-3">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="fanfav-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by hip #, horse, or trainer"
              className="h-12 w-full rounded-xl border border-border bg-card pl-10 pr-4 outline-none focus:ring-2 focus:ring-secondary" />
          </div>

          {!divOpen && <p className="mt-3 rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800">{LABEL[division]} voting is closed.</p>}

          {list.length === 0 && <p data-testid="fanfav-empty" className="mt-3 text-sm text-muted-foreground">No {LABEL[division]} horses to vote for yet.</p>}
          <div className="mt-3 grid grid-cols-2 gap-3 pb-4">
            {list.map((h) => {
              const votedThis = status?.[division]?.used;
              const isMine = status?.[division]?.horse?.id === h.id;
              return (
                <div key={h.id} data-testid={`fanfav-card-${h.hip_number}`} className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                  <div className="relative aspect-[4/3] bg-muted">
                    {h.photo ? <img src={h.photo} alt={h.horse_name} className="h-full w-full object-cover" />
                      : <div className="grid h-full w-full place-items-center bg-gradient-to-br from-primary/10 to-secondary/10"><span className="font-display text-3xl font-bold text-primary/25">HIP #{h.hip_number}</span></div>}
                    <span className="absolute left-2 top-2 rounded bg-primary/90 px-1.5 py-0.5 text-[11px] font-bold text-primary-foreground">HIP #{h.hip_number}</span>
                  </div>
                  <div className="flex flex-1 flex-col p-2.5">
                    <p className="font-display font-bold leading-tight text-primary">{h.horse_name}</p>
                    <p className="text-xs text-muted-foreground">{h.trainer_name}</p>
                    {votedThis ? (
                      <span className={`mt-2 inline-flex items-center justify-center gap-1 rounded-full px-2 py-2 text-xs font-bold ${isMine ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"}`}>
                        <CheckCircle2 size={13} /> {isMine ? "Your Pick" : "Vote Recorded"}
                      </span>
                    ) : (
                      <button data-testid={`fanfav-vote-${h.hip_number}`} disabled={!divOpen} onClick={() => vote(h)}
                        className="mt-2 h-9 rounded-full bg-secondary text-sm font-bold text-white active:scale-95 disabled:opacity-50">Vote</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </AppLayout>
  );
}

const WinnerCard = ({ label, horse }) => (
  <div data-testid={`winner-${label.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-3 rounded-xl border-2 border-secondary bg-secondary/5 p-3">
    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-muted">
      {horse.photo ? <img src={horse.photo} alt={horse.horse_name} className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center text-secondary"><Trophy size={22} /></div>}
    </div>
    <div>
      <p className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-secondary"><Trophy size={13} /> {label} Winner</p>
      <p className="font-display text-lg font-bold text-primary">HIP #{horse.hip_number} · {horse.horse_name}</p>
      <p className="text-xs text-muted-foreground">{horse.trainer_name}</p>
    </div>
  </div>
);
